import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Loader2, Search, ShieldCheck, Users, ChevronRight } from "lucide-react";
import LSSDLogo from "../components/LSSDLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Roster() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/public/roster`)
      .then(({ data }) => setData(data))
      .catch(() => setData({ total: 0, groups: [] }))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    const needle = q.trim().toLowerCase();
    if (!needle) return data.groups;
    return data.groups
      .map((g) => ({
        ...g,
        operators: g.operators.filter(
          (o) =>
            (o.rp_name || "").toLowerCase().includes(needle) ||
            (o.badge_number || "").toLowerCase().includes(needle) ||
            (g.role || "").toLowerCase().includes(needle)
        ),
      }))
      .filter((g) => g.operators.length > 0);
  }, [data, q]);

  if (loading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center bg-[#0A1F18]" data-testid="roster-loading">
        <Loader2 className="h-8 w-8 animate-spin text-sheriff-gold" />
      </div>
    );
  }

  const total = data?.total || 0;
  const visibleCount = filtered.reduce((n, g) => n + g.operators.length, 0);

  return (
    <div className="min-h-screen bg-[#0A1F18] text-[#F5E6B8] relative overflow-hidden" data-testid="roster-page">
      <div className="film-grain absolute inset-0 pointer-events-none opacity-40" />
      {/* Watermark */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden">
        <div className="text-sheriff-gold/[0.025] font-heading font-black text-[220px] rotate-[-18deg] tracking-tighter select-none whitespace-nowrap">
          ORGANICO
        </div>
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-16 space-y-10">
        {/* HEADER */}
        <div className="space-y-4">
          <div className="uppercase-label text-sheriff-gold flex items-center gap-2 text-xs tracking-[0.3em]">
            <LSSDLogo className="h-6 w-6" />
            <span>§ Directory ufficiale</span>
          </div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-[#F5E6B8] leading-[0.9]" data-testid="roster-title">
            Organico <span className="text-sheriff-gold italic font-medium">LSSD</span>
          </h1>
          <p className="text-[#B8A269] max-w-2xl text-sm sm:text-base">
            Consulta l&apos;elenco pubblico degli operatori attivi del Los Santos Sheriff Department,
            organizzato per grado. Clicca su un operatore per visualizzarne il dossier ufficiale.
          </p>
          <div className="flex items-center gap-6 text-[#8B7B49] text-xs uppercase-label tracking-wider flex-wrap">
            <span className="inline-flex items-center gap-2">
              <Users className="h-4 w-4 text-sheriff-gold" />
              <span data-testid="roster-total">
                {total} operatori totali
              </span>
            </span>
            {q && (
              <span data-testid="roster-visible-count">Risultati: {visibleCount}</span>
            )}
          </div>
        </div>

        {/* SEARCH */}
        <div className="relative max-w-md">
          <Search className="h-4 w-4 absolute left-4 top-1/2 -translate-y-1/2 text-sheriff-gold/70" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca per nome, matricola o grado..."
            data-testid="roster-search"
            className="w-full pl-11 pr-4 py-3 rounded-full bg-[#143830] border border-sheriff-gold/30 text-[#F5E6B8] placeholder:text-[#8B7B49] focus:outline-none focus:border-sheriff-gold/70 focus:ring-1 focus:ring-sheriff-gold/40 transition-all"
          />
        </div>

        {/* GROUPS */}
        {filtered.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-sheriff-gold/20 rounded-2xl">
            <ShieldCheck className="h-10 w-10 mx-auto text-sheriff-gold/30 mb-3" />
            <div className="uppercase-label text-sheriff-gold/70 tracking-wider">Nessun operatore trovato</div>
          </div>
        ) : (
          <div className="space-y-10">
            {filtered.map((g) => (
              <section key={g.role} data-testid={`roster-group-${g.role}`}>
                <div className="flex items-baseline justify-between border-b border-sheriff-gold/15 pb-2 mb-4">
                  <h2 className="font-heading text-lg sm:text-xl text-sheriff-gold tracking-tight">
                    {g.role}
                  </h2>
                  <span className="dossier-mono text-[10px] uppercase tracking-[0.25em] text-sheriff-gold/50">
                    {g.operators.length} in servizio
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {g.operators.map((op) => (
                    <Link
                      key={op.slug}
                      to={`/operatore/${op.slug}`}
                      data-testid={`roster-operator-${op.slug}`}
                      className="group flex items-center gap-3 rounded-xl bg-gradient-to-br from-[#143830] to-[#0F2620] border border-sheriff-gold/20 hover:border-sheriff-gold/50 px-4 py-3 transition-all duration-200 hover:-translate-y-0.5"
                    >
                      <div className="h-11 w-11 rounded-full bg-sheriff-gold/10 border border-sheriff-gold/40 flex items-center justify-center overflow-hidden shrink-0">
                        {op.discord_id ? (
                          <img
                            src={`${API}/discord/user/${op.discord_id}/avatar`}
                            alt={op.rp_name}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                          />
                        ) : (
                          <ShieldCheck className="h-5 w-5 text-sheriff-gold/60" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-[#F5E6B8] font-medium text-sm truncate group-hover:text-sheriff-gold transition-colors">
                          {op.rp_name}
                        </div>
                        <div className="dossier-mono text-[10px] uppercase tracking-[0.2em] text-sheriff-gold/60 mt-0.5 truncate">
                          {op.badge_number ? `Matricola ${op.badge_number}` : "— matricola non assegnata"}
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-sheriff-gold/40 group-hover:text-sheriff-gold group-hover:translate-x-0.5 transition-all shrink-0" />
                    </Link>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
